import { TagModel } from 'models';

/**
 * Flattens a hierarchical tag structure into a flat array
 * @param tags - Hierarchical tag array
 * @returns Flat array of tags without children property
 */
export const flattenTags = (tags: TagModel[]): TagModel[] => {
  return tags.reduce((acc: TagModel[], tag) => {
    const { children, ...tagWithoutChildren } = tag;
    acc.push(tagWithoutChildren);
    if (children) {
      acc.push(...flattenTags(children));
    }
    return acc;
  }, []);
};

/**
 * Creates a map of tag ID to tag object from flat tags
 * @param flatTags - Flat array of tags
 * @returns Map with tag ID as key and tag object as value
 */
export const createTagIdToTagMap = (flatTags: TagModel[]): Map<number, TagModel> => {
  const map = new Map<number, TagModel>();
  flatTags.forEach((tag) => {
    map.set(tag.id, tag);
  });
  return map;
};

/**
 * Creates a map of tag name to tag ID from flat tags
 * @param flatTags - Flat array of tags
 * @returns Map with lowercase tag name as key and tag ID as value
 */
export const createTagNameToIdMap = (flatTags: TagModel[]): Map<string, number> => {
  const map = new Map<string, number>();
  flatTags.forEach((tag) => {
    map.set(tag.name.toLowerCase(), tag.id);
  });
  return map;
};

/**
 * Processes hierarchical tags and returns all derived maps
 * @param hierarchicalTags - Hierarchical tag structure
 * @returns Object containing all tag maps and arrays
 */
export const processTagData = (hierarchicalTags: TagModel[]) => {
  const allFlatTags = flattenTags(hierarchicalTags);
  const tagIdToTagMap = createTagIdToTagMap(allFlatTags);
  const tagNameToIdMap = createTagNameToIdMap(allFlatTags);

  return {
    allHierarchicalTags: hierarchicalTags,
    allFlatTags,
    tagIdToTagMap,
    tagNameToIdMap,
  };
};

// Helper function to get all descendant tag IDs (including self)
export const getAllDescendantTagIds = (tagId: number, allTags: TagModel[]): number[] => {
  // Function to efficiently find a tag by ID within a potentially deep hierarchy
  const findTag = (id: number): TagModel | undefined => {
    const queue = [...allTags]; // Start with top-level tags
    while (queue.length > 0) {
      const current = queue.shift();
      if (!current) continue;
      if (current.id === id) return current;
      if (current.children) queue.push(...current.children);
    }
    return undefined;
  };

  const tag = findTag(tagId);
  if (!tag) return [];

  let descendantIds: number[] = [tagId];
  if (tag.children) {
    // Recursively get descendants for each child
    tag.children.forEach((child) => {
      // Pass the *original* full list for lookups in recursive calls
      descendantIds = [...descendantIds, ...getAllDescendantTagIds(child.id, allTags)];
    });
  }
  return descendantIds;
};

// Helper function to get all parent tag IDs (including self)
// Requires a FLAT list of tags for efficient lookup
export const getAllParentTagIds = (tagId: number, allTagsFlat: TagModel[]): number[] => {
  const tagMap = new Map(allTagsFlat.map((t) => [t.id, t]));
  const parentIds: number[] = [];
  let currentTag = tagMap.get(tagId);
  while (currentTag) {
    parentIds.push(currentTag.id);
    // Navigate up using parentTagId
    currentTag = currentTag.parentTagId ? tagMap.get(currentTag.parentTagId) : undefined;
  }
  return parentIds;
};

// Filters a hierarchical tag tree to include only nodes present in allowedIds
// or nodes that have descendants present in allowedIds.
export const filterTreeByAllowedIds = (tags: TagModel[], allowedIds: Set<number>): TagModel[] => {
  return tags
    .map((tag): TagModel | null => {
      const filteredChildren = tag.children ? filterTreeByAllowedIds(tag.children, allowedIds) : [];
      // Keep the node if it's directly allowed OR if any of its children are kept
      if (allowedIds.has(tag.id) || filteredChildren.length > 0) {
        return {
          ...tag,
          children: filteredChildren,
        };
      }
      return null;
    })
    .filter((node): node is TagModel => node !== null);
};

// Aggregates counts for tags including counts of all their descendants.
// Takes a hierarchical tag structure and a map of direct counts for each tag.
export const aggregateTagCounts = (tags: TagModel[], directCounts: Map<number, number>): Map<number, number> => {
  const aggregatedCounts = new Map<number, number>();

  const calculateCountsRecursive = (tag: TagModel): number => {
    // Memoization check: If already calculated, return stored value
    if (aggregatedCounts.has(tag.id)) {
      // This case might not be hit often with top-down recursion, but good practice
      // return aggregatedCounts.get(tag.id)!;
    }

    let totalCount = directCounts.get(tag.id) ?? 0;
    if (tag.children) {
      tag.children.forEach((child) => {
        totalCount += calculateCountsRecursive(child);
      });
    }
    aggregatedCounts.set(tag.id, totalCount);
    return totalCount;
  };

  tags.forEach((tag) => calculateCountsRecursive(tag));
  return aggregatedCounts;
};

// Assigns counts from a map to the corresponding tags in a hierarchical structure.
export const assignCurrentCountsToTree = (tags: TagModel[], currentCounts: Map<number, number>): TagModel[] => {
  return tags.map((tag) => {
    const count = currentCounts.get(tag.id) ?? 0;
    // Recursively assign counts to children
    const childrenWithCounts = tag.children ? assignCurrentCountsToTree(tag.children, currentCounts) : [];
    return {
      ...tag,
      count: count, // Add/update the count property
      children: childrenWithCounts,
    };
  });
};
